import React, { Fragment, useMemo, useState } from "react";
import PropTypes from "prop-types";
import BWModels from "@benjamin-west-rpm2/benjamin-west-models";
import styled from "styled-components";
import { Divider } from "@material-ui/core";

import { amountRequestedFields, noteField } from "../fundingFields";
import FormGrid from "../../../../../../forms/FormGrid";
import FormikForm from "../../../../../../forms/FormikForm";
import propTypes from "../../../../../../../constants/propTypes";

import { useV2DatacomponentResources } from "../../../../../../hooks/useResources";
import * as RequestTypes from "../../../../../../../constants/RequestTypes";
import { cleanCurrencyValue } from "../../../../../../../utils/currencyFormatter";
import { FundingFormTitle } from "./FundingFormTitle";
import { CashflowDocuments } from "./CashflowDocuments";
import SubmitButton from "./SubmitButton";

const Content = styled.div`
  overflow-y: auto;
  max-height: 58vh;
`;

export const handleFundingCurrencyChange = (
  values,
  setFieldValue,
  projectCurrencies,
  setChangingCurrency
) => event => {
  const value = event.target.value || 0;
  const selectedProjectCurrency = projectCurrencies.find(
    projectCurrency => projectCurrency.id == value
  );

  setChangingCurrency(true);

  setFieldValue("projectCurrency", selectedProjectCurrency);

  setChangingCurrency(false);
};

export const handleCurrencyInputChange = (
  setFieldValue,
  selectedProjectCurrency,
  changingCurrency,
  setChangingCurrency
) => (event, fieldName) => {
  if (!selectedProjectCurrency) return;
  if (changingCurrency) {
    setChangingCurrency(false);
    return;
  }
  const value = event.target.value || 0;

  const newFieldNameValue = cleanCurrencyValue(value);

  setFieldValue(fieldName, newFieldNameValue);
};

const FundingForm = ({
  onSubmit,
  initialValues,
  dataComponentId,
  loading,
  hideCancelButton,
  onCancel,
  sendButtonText,
  closeModalDialog,
  isFundingPaidConfirmation,
}) => {
  const [changingCurrency, setChangingCurrency] = useState(false);

  const modifiedSchema = useMemo(
    () =>
      BWModels.loadSchema("Funding").append({
        cashflowFile: BWModels.Joi.when("method", {
          is: "Cashflow",
          then: BWModels.loadSchema("File").required(),
          otherwise: BWModels.loadSchema("File"),
        }),
        projectBudgetFile: BWModels.Joi.when("method", {
          is: "Cashflow",
          then: BWModels.loadSchema("File").required(),
          otherwise: BWModels.loadSchema("File"),
        }),
      }),
    []
  );

  const projectCurrencies = useV2DatacomponentResources(
    "select-project-currencies",
    [],
    RequestTypes.LIST
  );

  return (
    <FormikForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      ignoreValidationFieldNames={[
        "type",
        "processingDate",
        "fundsReceived",
        "paymentRecord",
      ]}
      validationSchema={modifiedSchema}
    >
      {({ handleSubmit, values, errors, setFieldValue, ...formikProps }) => {
        const selectedProjectCurrency = projectCurrencies.find(
          projectCurrency => projectCurrency.id == values.projectCurrencyId
        );
        return (
          <Fragment>
            <FundingFormTitle
              closeModalDialog={closeModalDialog}
              isFundingPaidConfirmation={isFundingPaidConfirmation}
            />
            <Content>
              <FormGrid
                fields={amountRequestedFields(
                  {
                    ...values,
                    selectedProjectCurrency,
                  },
                  handleFundingCurrencyChange(
                    values,
                    setFieldValue,
                    projectCurrencies,
                    setChangingCurrency
                  ),
                  handleCurrencyInputChange(
                    setFieldValue,
                    selectedProjectCurrency,
                    changingCurrency,
                    setChangingCurrency
                  )
                )}
                values={values}
                errors={errors}
                {...formikProps}
              />
              <CashflowDocuments />
              <Divider />
              <FormGrid
                fields={noteField}
                values={values}
                errors={errors}
                {...formikProps}
              />
              <SubmitButton
                dataComponentId={dataComponentId}
                loading={loading}
                handleSubmit={handleSubmit}
                hideCancelButton={hideCancelButton}
                onCancel={onCancel}
                sendButtonText={sendButtonText}
              />
            </Content>
          </Fragment>
        );
      }}
    </FormikForm>
  );
};

FundingForm.propTypes = {
  onSubmit: PropTypes.func,
  isUpdate: PropTypes.bool,
  initialValues: propTypes.glCode,
  isFundingPaidConfirmation: PropTypes.bool,
};

FundingForm.defaultProps = {
  sendButtonText: "Issue Funding Request",
};

export default FundingForm;
